import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toAbsoluteUrl } from "sr/helpers";
import { ModelBottomItem } from "./SidebarItems";
import { FormattedMessage } from "react-intl";
import { getUserDetails } from "sr/helpers/functions/getUserDetails";
import { useAuth } from "app/pages/module/auth";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "sr/redux/store";
import { useSocket } from "app/pages/component/SocketContext";
import { formatISO } from "date-fns";
import { DaysConverter } from "sr/helpers/functions/DaysConverter";
import {
  fetchChat,
  removeNotificationMessage,
  updateSystemMessage,
} from "sr/redux/reducers/notification";
import { updateMessage } from "sr/utils/api/cleaner/updateMessage";
import { ConditionalWrapper } from "app/pages/module/auth/ConditionalWrapper";
import { NotificationLink } from "sr/helpers/functions/NotificationLink";
import {
  fetchCompanyChat,
  updateUnreadChatCount,
} from "sr/redux/reducers/companychat";
import { fetchCorporate } from "sr/redux/reducers/corporate";

export const CorporateHeader = (props) => {
  const { logout, userType } = useAuth();
  const [model, setModel] = useState(false);
  const [userId, setUserId] = useState<string | undefined>();
  const [showEffect, setShowEffect] = useState(false);
  const [notificationPopup, setNotificationPopup] = useState(false);

  const messageData = useSelector((state: any) => state.chat.data);
  const messageDataRef = useRef(messageData);
  const unreadMessages = useSelector((state: any) => state.companychat.data);
  const corporateData = useSelector((state: any) => state.corporate.data);

  const dispatch = useDispatch<AppDispatch>();
  const audioFile = toAbsoluteUrl(
    "/media/notification/notification-18-270129.mp3"
  );

  // Remove notifications
  const handleRemove = async (id?: any) => {
    try {
      const payload = { id, readed: true };
      const updateMessagePayload = messageData?.map((item: any) => ({
        ...item,
        readed: true,
      }));
      const res = await updateMessage(id ? payload : updateMessagePayload);
      if (res?.success === true) {
        dispatch(removeNotificationMessage({ id }));
      }
    } catch (e) {
      console.error("Failed to mark notification as read", e);
    }
  };

  // Get user details from localStorage
  useEffect(() => {
    const details = getUserDetails();
    setUserId(details?.user_id);
  }, []);

  // Fetch corporate profile details
  useEffect(() => {
    if (userId && userType === "corporate" && !corporateData) {
      dispatch(fetchCorporate({ id: userId }));
    }
  }, [userId, userType]);

  // Toggle notifications popup
  const handleNotification = () => {
    if (model) setModel(false);
    if (!notificationPopup) {
      setNotificationPopup(true);
      setTimeout(() => setShowEffect(true), 200);
    } else {
      setTimeout(() => setShowEffect(false), 200);
      setTimeout(() => setNotificationPopup(false), 500);
    }
  };

  // Toggle profile dropdown
  const handleModel = () => {
    if (notificationPopup) setNotificationPopup(false);
    if (!model) {
      setModel(true);
      setTimeout(() => setShowEffect(true), 200);
    } else {
      setTimeout(() => setShowEffect(false), 200);
      setTimeout(() => setModel(false), 500);
    }
  };

  // Keep ref updated
  useEffect(() => {
    messageDataRef.current = messageData;
  }, [messageData]);

  // Socket handling for corporate
  const socket = useSocket();
  useEffect(() => {
    if (!userId || userType !== "corporate") return;
    if (socket) {
      socket.emit("register", userId);
    }

    socket.on("sendmessage", ({ messagewithFullData: message }) => {
      if (message) {
        new Audio(audioFile).play().catch(console.error);
        if (message?.sender_id_type === "cleaner") {
          dispatch(updateUnreadChatCount(message));
        }
      }
    });

    socket.on("notification", (message) => {
      if (message) {
        new Audio(audioFile).play().catch(console.error);
        const newMessage = { createdAt: formatISO(new Date()), ...message };
        dispatch(
          updateSystemMessage([...(messageDataRef.current || []), newMessage])
        );
      }
    });
  }, [userId]);

  // Sort messages
  const sortedMessage = [...(messageData || [])].sort(
    (a, b) => new Date(b?.createdAt).getTime() - new Date(a?.createdAt).getTime()
  );

  return (
    <nav
      className={`${props.isOpen ? "pl-48" : "pl-16"
        } top-0 sticky z-20 duration-300 pr-4 border border-b-text-gray-500`}
    >
      <div className="w-full flex bg-white justify-between py-1.5">
        {/* Logo */}
        <div>
          <img
            width={120}
            src={toAbsoluteUrl("/media/logos/logo2.png")}
            alt="logo"
          />
        </div>

        {/* Right Menu */}
        <div className="lg:flex block space-x-4">
          {/* Help */}
          <Link className="flex items-center space-x-2" to="#">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 20 20"
              className="w-5 h-5"
            >
              <path
                fillRule="evenodd"
                d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 5.497a1.25 1.25 0 00-1.152.763c-.107.254-.322.487-.598.487h-.5c-.276 0-.505-.226-.455-.498a2.752 2.752 0 015.455.498c0 .779-.369 1.326-.765 1.714-.19.186-.399.349-.608.511-.162.126-.324.251-.477.387a.538.538 0 01-.357.142H9.75c-.276 0-.507-.231-.412-.49.137-.374.42-.687.736-.94.088-.072.192-.144.3-.22.405-.281.876-.609.876-1.104A1.25 1.25 0 0010 7.497zM9.25 13.5a.5.5 0 01.5-.5h.5a.5.5 0 01.5.5v1a.5.5 0 01-.5.5h-.5a.5.5 0 01-.5-.5v-1z"
                clipRule="evenodd"
              />
            </svg>
            <span>
              <FormattedMessage
                id="CORPORATE.TOPBAR.MENU.HELP"
                defaultMessage="Help"
              />
            </span>
          </Link>

          {/* Notifications */}
          <div
            onClick={handleNotification}
            className="flex items-center w-full space-x-2 relative cursor-pointer"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 20 20"
              className="w-5 h-5"
            >
              <path d="M10 2a1.5 1.5 0 00-1.5 1.5v.229A5.002 5.002 0 005 8.5V12l-1.854 1.854a.5.5 0 00-.146.353v.293a.5.5 0 00.5.5h13a.5.5 0 00.5-.5v-.293a.5.5 0 00-.146-.353L15 12V8.5a5.002 5.002 0 00-3.5-4.771V3.5A1.5 1.5 0 0010 2zm.002 16a2.5 2.5 0 01-2.45-2h4.9a2.5 2.5 0 01-2.45 2z"></path>
            </svg>
            <span>
              <FormattedMessage
                id="CORPORATE.TOPBAR.MENU.NOTIFICATION"
                defaultMessage="Notification"
              />
            </span>
            {messageData?.length > 0 && (
              <div className="rounded-full bg-[#ff39b3] font-bold w-4 h-4 flex justify-center items-center absolute top-0 -left-1">
                <span className="text-[0.8rem] text-white">
                  {messageData?.length}
                </span>
              </div>
            )}

            {/* Notification Popup */}
            <div
              className={`dropdown-menu transitioncss fixed ${notificationPopup && sortedMessage?.length > 0
                  ? "block"
                  : "hidden"
                } ${showEffect ? "modeleffect" : "popup"} w-[450px] right-10 top-10 z-50 max-h-[500px] overflow-y-scroll rounded-lg border`}
            >
              <div className="bg-white shadow py-3 rounded-lg text-black">
                <p
                  onClick={() => handleRemove()}
                  className="text-right text-sm font-bold text-[#0078ae] pr-2 cursor-pointer"
                >
                  Mark all as read
                </p>
                {sortedMessage?.map((item, index) => (
                  <ConditionalWrapper
                    condition={NotificationLink(item)}
                    key={index}
                  >
                    <div className="flex items-center space-x-2 relative px-2 py-2 border-b">
                      <div className="w-12 h-12">
                        <img
                          className="w-12 h-12 rounded-full"
                          src={toAbsoluteUrl(
                            "/media/icons/displayProfilePlaceholder.png"
                          )}
                          alt="profile"
                        />
                      </div>
                      <div className="flex-1">
                        <span className="text-xs flex justify-end">
                          {DaysConverter(item?.createdAt)} Days Ago
                        </span>
                        <p className="text-sm">{item?.message?.slice(0, 100)}</p>
                      </div>
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleRemove(item?._id);
                        }}
                        className="absolute top-2 right-2 cursor-pointer"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="w-4 h-4"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M6 18 18 6M6 6l12 12"
                          />
                        </svg>
                      </div>
                    </div>
                  </ConditionalWrapper>
                ))}
              </div>
            </div>
          </div>

          {/* Messages */}
          <Link
            className="flex items-center space-x-2 border-r border-text-gray-500 pr-4 relative"
            to="/messages"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 20 20"
              className="w-5 h-5"
            >
              <path
                fillRule="evenodd"
                d="M2.5 4a.5.5 0 00-.5.5v11a.5.5 0 00.5.5h15a.5.5 0 00.5-.5v-11a.5.5 0 00-.5-.5h-15zm13.111 2.26L10 10 4.389 6.26A.25.25 0 004 6.466v.9a.25.25 0 00.111.207L10 11.5l5.889-3.926A.25.25 0 0016 7.366v-.899a.25.25 0 00-.389-.208z"
                clipRule="evenodd"
              />
            </svg>
            <span>
              <FormattedMessage
                id="CORPORATE.TOPBAR.MENU.MESSAGES"
                defaultMessage="Messages"
              />
            </span>
            {unreadMessages?.length > 0 && (
              <div className="rounded-full bg-[#ff39b3] font-bold w-4 h-4 flex justify-center items-center absolute top-[0.25rem] -left-[0.4rem]">
                <span className="text-[0.8rem] text-white">
                  {unreadMessages?.length}
                </span>
              </div>
            )}
          </Link>

          {/* Profile */}
          <Link
            className="flex items-center space-x-2 relative"
            to="#"
            onClick={handleModel}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 24 24"
              className="w-5 h-5"
            >
              <path d="M12 12c2.486 0 4.5-2.014 4.5-4.5S14.486 3 12 3a4.499 4.499 0 00-4.5 4.5C7.5 9.986 9.514 12 12 12zm0 2.25c-3.004 0-9 1.508-9 4.5v1.75a.5.5 0 00.5.5h17a.5.5 0 00.5-.5v-1.75c0-2.992-5.996-4.5-9-4.5z"></path>
            </svg>
            <span className="max-w-[150px] truncate">
              {corporateData?.username}
            </span>
            <span
              className={`transition-transform transform duration-1000 ${model ? "rotate-180" : ""
                }`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-3 h-3"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                />
              </svg>
            </span>

            {/* Profile Dropdown */}
            {model && (
              <div
                className={`${showEffect ? "modeleffect" : "popup"
                  } w-[22rem] transitioncss z-50 absolute top-14 right-2 bg-white rounded-lg border border-slate-400`}
              >
                <div className="p-4">
                  <div className="py-2">
                    <h1 className=" text-lg">{corporateData?.username}</h1>
                    <p className="text-gray-500">{corporateData?.email}</p>
                  </div>
                  <ul>
                    {ModelBottomItem.map((item, index) => (
                      <Link key={index} to={item.link ? item.link : "#"}>
                        <li className="flex items-center space-x-2 py-3">
                          {item.icon}
                          <span>{item.text}</span>
                        </li>
                      </Link>
                    ))}
                    <li
                      onClick={logout}
                      className="flex items-center space-x-2 py-3 cursor-pointer"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                        className="w-5 h-5"
                      >
                        <path d="M4 3a2 2 0 00-2 2v9.998a2 2 0 002 2h5.5a.5.5 0 00.5-.5v-.5a.5.5 0 00-.5-.5h-6v-11h6a.5.5 0 00.5-.5V3.5a.5.5 0 00-.5-.5H4z"></path>
                        <path d="M12.274 13.795a.5.5 0 000 .707l.354.353a.5.5 0 00.707 0l4.52-4.52a.503.503 0 00-.004-.71l-4.477-4.48a.5.5 0 00-.707 0l-.354.354a.5.5 0 000 .707l3.041 3.043H7.517a.5.5 0 00-.5.5v.5a.5.5 0 00.5.5h7.803l-3.046 3.046z"></path>
                      </svg>
                      <span>
                        <FormattedMessage
                          id="CORPORATE.TOPBAR.MENU.SIGN.OUT"
                          defaultMessage="Sign Out"
                        />
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </Link>
        </div>
      </div>

      {/* Backdrop */}
      {(model || notificationPopup) && (
        <span
          onClick={() => {
            notificationPopup && handleNotification();
            model && handleModel();
          }}
          className="fixed w-full h-full z-[40] top-0 left-0 delay-100 visible"
        ></span>
      )}
    </nav>
  );
};
